import { useEffect } from "react";
import type { ReactNode } from "react";
import {
  BoldIcon,
  ItalicIcon,
  ListBulletIcon,
  NumberedListIcon,
} from "@heroicons/react/24/outline";
import { Markdown } from "@tiptap/markdown";
import { Editor, EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { twMerge } from "tailwind-merge";

import { getCharactersCount } from "~/common/utils";
import { ErrorText } from "./typography";

interface MarkdownEditorProps {
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  disabled?: boolean;
  error?: string;
  maxCharacters?: number;
  charactersCount?: number;
  height?: number;
  className?: string;
  id?: string;
}

interface ToolbarButtonProps {
  label: string;
  isActive?: boolean;
  disabled?: boolean;
  onClick: () => void;
  children: ReactNode;
}

const ToolbarButton = ({
  label,
  isActive,
  disabled,
  onClick,
  children,
}: ToolbarButtonProps) => (
  <button
    type="button"
    aria-label={label}
    disabled={disabled}
    onClick={onClick}
    className={twMerge(
      "rounded p-1.5 text-gray-35 transition-colors hover:bg-slate-100 hover:text-gray-70 disabled:cursor-not-allowed disabled:opacity-50",
      isActive && "bg-slate-100 text-gray-70",
    )}
  >
    {children}
  </button>
);

interface RichTextToolbarProps {
  editor: Editor;
  disabled?: boolean;
}

const RichTextToolbar = ({ editor, disabled }: RichTextToolbarProps) => (
  <div className="flex flex-wrap items-center gap-0.5 border-b border-gray-300 px-2 py-1.5">
    <ToolbarButton
      label="Bold"
      disabled={disabled}
      isActive={editor.isActive("bold")}
      onClick={() => editor.chain().focus().toggleBold().run()}
    >
      <BoldIcon className="size-4" />
    </ToolbarButton>
    <ToolbarButton
      label="Italic"
      disabled={disabled}
      isActive={editor.isActive("italic")}
      onClick={() => editor.chain().focus().toggleItalic().run()}
    >
      <ItalicIcon className="size-4" />
    </ToolbarButton>
    <span className="mx-1 h-5 w-px bg-gray-300" aria-hidden />
    <ToolbarButton
      label="Heading 2"
      disabled={disabled}
      isActive={editor.isActive("heading", { level: 2 })}
      onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
    >
      <span className="px-0.5 text-xs font-semibold">H2</span>
    </ToolbarButton>
    <ToolbarButton
      label="Heading 3"
      disabled={disabled}
      isActive={editor.isActive("heading", { level: 3 })}
      onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
    >
      <span className="px-0.5 text-xs font-semibold">H3</span>
    </ToolbarButton>
    <span className="mx-1 h-5 w-px bg-gray-300" aria-hidden />
    <ToolbarButton
      label="Bullet list"
      disabled={disabled}
      isActive={editor.isActive("bulletList")}
      onClick={() => editor.chain().focus().toggleBulletList().run()}
    >
      <ListBulletIcon className="size-4" />
    </ToolbarButton>
    <ToolbarButton
      label="Numbered list"
      disabled={disabled}
      isActive={editor.isActive("orderedList")}
      onClick={() => editor.chain().focus().toggleOrderedList().run()}
    >
      <NumberedListIcon className="size-4" />
    </ToolbarButton>
  </div>
);

export const MarkdownEditor = ({
  value,
  onChange,
  onBlur,
  disabled = false,
  error,
  maxCharacters,
  charactersCount = 0,
  height = 450,
  className,
  id,
}: MarkdownEditorProps) => {
  const editor = useEditor({
    extensions: [StarterKit, Markdown],
    content: value,
    contentType: "markdown",
    editable: !disabled,
    shouldRerenderOnTransaction: true,
    editorProps: {
      attributes: {
        id,
        class: twMerge(
          "prose prose-sm max-w-none px-2.5 py-2 text-sm text-gray-70 focus:outline-none",
          "[&_h2]:text-lg [&_h3]:text-base",
        ),
        style: `min-height: ${height - 45}px`,
      },
    },
    onUpdate: ({ editor: currentEditor }) => {
      onChange(currentEditor.getMarkdown());
    },
    onBlur: () => {
      onBlur?.();
    },
  });

  useEffect(() => {
    if (!editor) return;

    editor.setEditable(!disabled);
  }, [disabled, editor]);

  useEffect(() => {
    if (!editor) return;

    const currentMarkdown = editor.getMarkdown();
    if (value !== currentMarkdown) {
      editor.commands.setContent(value, {
        contentType: "markdown",
        emitUpdate: false,
      });
    }
  }, [editor, value]);

  return (
    <div
      className={twMerge(
        "flex w-full flex-col gap-2 font-light text-gray-70",
        className,
      )}
    >
      <div
        className={twMerge(
          "overflow-hidden rounded-md border border-gray-300 bg-neutral-25",
          disabled && "bg-gray-200 text-gray-700",
          error && "border-error bg-red-50",
        )}
      >
        {editor && !disabled && <RichTextToolbar editor={editor} />}
        <EditorContent editor={editor} />
      </div>
      <div
        className={twMerge(
          "flex items-start justify-between gap-3",
          !error && "justify-end",
        )}
      >
        {error && <ErrorText>{error}</ErrorText>}
        {maxCharacters && (
          <span className="mr-1 text-xs">
            {getCharactersCount(charactersCount, maxCharacters)}
          </span>
        )}
      </div>
    </div>
  );
};
